import TagManager from 'react-gtm-module';

import {
  NUM_REC_VOTE_DECISION,
  VOTE_DECISION,
} from '../../../constants/vote-decisions';
import { COMMAND_MODES } from '../constants/command';

/**
 * Refreshes the user session in Google Analytics.
 */
const resetGoogleAnalyticsSession = () => {
  // Temporarily disable in lieu of migrating to GTM.
  return undefined;
};

/**
 * Gets the event action string for XCOMM voting.
 *
 * @param {Object} options The options.
 * @param {COMMAND_MODES} options.mode The mode (matcher or flusher).
 * @param {VOTE_DECISION|NUM_REC_VOTE_DECISION} options.decision The vote decision.
 * @param {boolean} options.notSure Whether the vote is a not sure or not.
 * @returns {string} The event action string.
 */
const getEventAction = ({ mode, decision, notSure }) => {
  if (!Object.values(COMMAND_MODES).includes(mode)) {
    throw Error('Please provide a valid COMMAND MODE');
  }

  if (
    decision === VOTE_DECISION.WUT ||
    decision === NUM_REC_VOTE_DECISION.NOT_SURE ||
    notSure
  ) {
    return 'Not Sure';
  }

  if (
    decision === VOTE_DECISION.NOPE ||
    decision === NUM_REC_VOTE_DECISION.NEGATIVE
  ) {
    return 'Not Even Close';
  }

  if (decision === VOTE_DECISION.TOTALLY && mode === COMMAND_MODES.MATCH) {
    return 'Match';
  }

  if (
    (decision === VOTE_DECISION.TOTALLY && mode === COMMAND_MODES.FLUSH) ||
    decision === NUM_REC_VOTE_DECISION.POSITIVE
  ) {
    return 'Flush';
  }

  return '-';
};

/**
 * Gets the event label string for XCOMM GA voting tracking.
 *
 * @param {Object} options The options.
 * @param {COMMAND_MODES} options.mode The mode (matcher or flusher).
 * @param {VOTE_DECISION} options.decision The vote decision.
 * @param {boolean} options.applied Applied status (flush or not).
 * @param {Array} options.votes The votes.
 * @param {boolean} options.notSure Whether the vote is a not sure or not.
 * @returns {string|undefined} The event label to be sent to GA.
 */
const getEventLabel = ({ mode, decision, applied, votes, notSure }) => {
  if (!Object.values(COMMAND_MODES).includes(mode)) {
    throw Error('Please provide a valid COMMAND MODE');
  }

  const eventAction = getEventAction({ mode, decision, notSure });

  // Matcher mode
  if (mode === COMMAND_MODES.MATCH) {
    if (decision === VOTE_DECISION.NOPE || decision === VOTE_DECISION.WUT) {
      return `${eventAction} (Matcher)`;
    }

    if (decision === VOTE_DECISION.TOTALLY && applied) {
      return 'Matchflush';
    }

    return 'Normal Match';
  }

  // Flusher mode
  if (mode === COMMAND_MODES.FLUSH) {
    const [vote] = votes;

    if (decision === VOTE_DECISION.NOPE || notSure) {
      return `${eventAction} (Flusher)`;
    }

    if (vote.vote === VOTE_DECISION.WUT && decision === VOTE_DECISION.TOTALLY) {
      return `${eventAction} (From MNS)`;
    }

    return `${eventAction} (From Match)`;
  }

  // NumRec Mode
  if (mode === COMMAND_MODES.NUM_REC) {
    if (decision === NUM_REC_VOTE_DECISION.POSITIVE) {
      return `${eventAction} (From NumRec)`;
    }

    return `${eventAction} (NumRec)`;
  }

  return undefined;
};

/**
 * Prepares an email address to be inserted into GA by making it
 * look less like an email.
 *
 * @param {string} email The email.
 * @returns {string} The email lookalike.
 */
const prepareEmailAddressForGA = email => {
  if (!email) {
    return '';
  }

  return email
    .replace('@', '+')
    .split('.')
    .slice(0, -1)
    .join('.')
    .toLowerCase();
};

/**
 * Tracks command events.
 *
 * @param {Object} options The options required for determining the event.
 * @param {Object} options.user The commander user account.
 * @param {string} options.decision The decision.
 * @param {boolean} options.applied Whether the vote decision should be applied (flushed) or not.
 * @param {boolean} options.notSure Whether the decision is "not sure".
 * @param {COMMAND_MODES} options.mode The command mode.
 * @param {Array<Object>} options.votes The vote to be voted on.
 * @param {Object} options.custom Any extra custom data we want to be passing to the event.
 * @param {string} [category] The event category name for GA.
 */
const trackCommandVoteEvent = (options, category = 'XCOMM Stats') => {
  const { user, decision, applied, notSure, mode, votes, custom } = options;

  if (!Object.values(COMMAND_MODES).includes(mode)) {
    throw Error('Please provide a valid COMMAND MODE');
  }

  const action = getEventAction({ mode, decision, notSure });
  const label = getEventLabel({ mode, decision, applied, votes, notSure });

  TagManager.dataLayer({
    dataLayer: {
      event: 'newEvent',
      newEventData: {
        type: category,
        action,
        label,
        value: votes.length,
        userEmail: user ? prepareEmailAddressForGA(user.email_address) : '',
        userId: user ? user.id : '',
        ...custom,
      },
    },
  });
};

export {
  resetGoogleAnalyticsSession,
  trackCommandVoteEvent,
  prepareEmailAddressForGA,
  getEventAction,
  getEventLabel,
};
